import React, { Fragment, useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
// import memoize from 'lodash/memoize';
// import sortByFn from 'lodash/sortBy';
// import orderBy from 'lodash/orderBy';

import { sort, filterByCountry, getTopCountries } from '~/utils/review';
import { actions as tripActions } from '~/redux/reducers/trip';
import { useUserCountry, useCurrentUser } from '~/utils/hooks/redux-store/session';

import LineLoader from '~/components/Loader/LineLoader';
import ReviewScore from './ReviewScore';
import ReviewFilter from './ReviewFilter';
import ReviewItem from './ReviewItem';
import ReviewMore from './ReviewMore';
import GalleryAllReview from '../General/Gallery/GalleryAllReview';

function useReviews() {
  const dispatch = useDispatch();
  const { reviews, isLoading, trip } = useSelector(state => ({
    reviews: state.trip.reviews,
    trip: state.trip.data.toJS ? state.trip.data.toJS() : state.trip.data,
    isLoading: state.trip.isLoadingReview,
  }));
  useEffect(() => {
    window.requestAnimationFrame(() => {
      dispatch(tripActions.getReviews(trip.slug));
    });
  }, []);
  return { reviews, trip, isLoading };
}

function useReviewActions(trip) {
  const dispatch = useDispatch();
  const onVote = useCallback((id, vote) => {
    dispatch(tripActions.voteReview(trip._id, id, vote));
  }, [trip]);
  const onReply = useCallback((id, text) => {
    dispatch(tripActions.replyReview(id, text));
  }, []);
  return { onVote, onReply };
}

function useReviewSortingAndFilter(reviews) {
  const [sortBy, setSortBy] = useState('relevance');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [showCount, setShowCount] = useState(3);
  const onChangeSorting = useCallback((newSortBy) => {
    setSortBy(newSortBy);
  }, []);
  const onChangeCountry = useCallback((country) => {
    setSelectedCountry(country || 'all');
  }, []);
  const onClickLoadMore = useCallback(() => {
    setShowCount(oldShowCount => (oldShowCount < reviews.length
      ? oldShowCount + 3
      : reviews.length
    ));
  }, [reviews]);
  return {
    sortBy,
    onChangeSorting,
    selectedCountry,
    onChangeCountry,
    showCount,
    onClickLoadMore,
  };
}

const Reviews = (props) => {
  const { reviews, trip, isLoading } = useReviews();
  const userCountry = useUserCountry();
  const user = useCurrentUser();
  const { selectedCountry, showCount, sortBy, onChangeSorting, onChangeCountry, onClickLoadMore } = useReviewSortingAndFilter(reviews);
  const { onReply, onVote } = useReviewActions(trip);

  const topCountries = getTopCountries(reviews, userCountry);
  const sortedReviews = sort(filterByCountry(reviews, selectedCountry), sortBy, trip._id);
  const filterReviews = sortedReviews.slice(0, showCount);
  const withoutReview = trip.travelers_without_review;

  const allImageReview = reviews
    .filter(c => c.images.length > 0)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .reduce((acc, curr) => [...acc, ...curr.images], []);

  if (isLoading) return <LineLoader />;
  if (reviews.length <= 0) return null;
  return (
    <Fragment>
      {/* Score */}
      <ReviewScore
        noCount={isLoading}
        reviews={reviews}
        trip={trip}
        rating={trip.rating}
        categoryScore={trip.review_score}
        withoutReview={withoutReview}
      />
      {/* Filter */}
      <ReviewFilter
        sortBy={sortBy}
        userId={trip.user_id}
        onChangeSort={onChangeSorting}
        onChangeCountry={onChangeCountry}
        selectedCountry={selectedCountry}
        topCountries={topCountries}
        userCountry={userCountry}
        reviews={reviews}
      />
      {/* Gallery */}
      <GalleryAllReview images={allImageReview} />
      {/* Items */}
      {filterReviews.map((review, index) => (
        <ReviewItem
          key={review._id || index}
          review={review}
          user={user}
          url={props.url}
          onVote={onVote}
          onReply={onReply}
        />
      ))}
      {/* More */}
      <ReviewMore
        reviews={reviews}
        lxInfo={trip.user_id}
        withoutReview={withoutReview}
        onClickLoadMore={onClickLoadMore}
        showLoadMore={showCount < sortedReviews.length}
      />
    </Fragment>
  );
};

export default Reviews;
